import { Status } from '~~/openapi-client/gen/schemas'
import type { IDnowResult } from '@verein-checkin/idnow'

const POLLING_INTERVAL_MS = 3000
const TERMINAL_STATUSES = [Status.SUCCESS, Status.FRAUD, Status.ABORTED, Status.OTHER] as const
const POLLING_ERROR_MESSAGE = 'Der Status Ihrer Legitimation konnte nicht abgerufen werden. Bitte laden Sie die Seite neu.'

/**
 * Composable zur Steuerung des IDnow-Videolegitimationsprozesses.
 *
 * Verwaltet den Verifikationsstatus, das automatische Status-Polling
 * sowie den Start der nativen IDnow-SDK-Identifikation.
 */
export default function useIdnow() {
	const { $api } = useNuxtApp()
	const { error: appError } = useApp()

	const isLoading = ref(false)

	/**
	 * Gemeinsamer Ablauf für beide Legitimationsverfahren:
	 * Auftrag erstellen, Startdaten laden, SDK starten, bei Erfolg Status setzen.
	 */
	const startIdentification = async (
		createOrder: () => Promise<unknown>,
		launchSdk: () => Promise<IDnowResult>,
	) => {
		isLoading.value = true
		try {
			if (!isLegitimationEnabled.value) {
				await $api.identityVerificationApi.setStatusInterim({ status: Status.SUCCESS })
				return { success: true }
			}

			await createOrder()
			await fetchStartData()

			const result = await launchSdk()

			if (result.success) {
				await $api.identityVerificationApi.setStatusInterim({ status: Status.REVIEW_PENDING })
				verificationData.value = { status: Status.REVIEW_PENDING }
			}

			return result
		} finally {
			isLoading.value = false
		}
	}

	/**
	 * Gibt an, ob die Legitimation (Video-Ident und AutoIdent) serverseitig aktiviert ist.
	 * Bei false werden beide Verfahren übersprungen und der Status direkt auf SUCCESS gesetzt
	 * (z. B. lokale Entwicklung, Demo-Systeme).
	 */
	const { data: isLegitimationEnabled } = useAsyncData(
		'idnow-enabled',
		() => $api.identityVerificationApi.isIdentityVerificationEnabled()
			.then(r => r.data),
		{ default: () => true },
	)

	/**
	 * Aktuelle Statusdaten des Nutzers inkl. AbortReason, AlreadyUsed, LastType.
	 * Wird beim Aufruf von `fetchStatus` aktualisiert.
	 */
	const { data: verificationData, refresh: fetchStatus } = useAsyncData(
		'idnow-verification-status',
		() => $api.identityVerificationApi.getStatus()
			.then(r => r.data),
		{ immediate: false },
	)

	const verificationStatus = computed(() => verificationData.value?.status as Status | undefined)

	/**
	 * Gibt an, ob der übergebene Status ein terminaler Status ist,
	 * bei dem das Polling beendet werden soll.
	 */
	const isTerminal = (status: Status | null | undefined) =>
		!!status && (TERMINAL_STATUSES as readonly Status[]).includes(status)

	/**
	 * Automatisches Status-Polling im definierten Intervall.
	 * Bei Erreichen eines terminalen Status oder bei einem Fehler wird das Polling gestoppt.
	 */
	const { isActive: isPolling, pause: stopPolling, resume: startPolling } = useIntervalFn(
		async () => {
			try {
				await fetchStatus()
				if (isTerminal(verificationStatus.value)) {
					stopPolling()
				}
			} catch {
				stopPolling()
				appError.value = POLLING_ERROR_MESSAGE
			}
		},
		POLLING_INTERVAL_MS,
		{ immediate: false, immediateCallback: false },
	)

	/**
	 * SDK-Startdaten vom Backend (registrierungsbezogen).
	 */
	const { data: startData, refresh: fetchStartData } = useAsyncData(
		'idnow-start-data',
		() => $api.identityVerificationApi.getStartDataForRegistration()
			.then(r => r.data),
		{ immediate: false },
	)

	/**
	 * Startet den Videolegitimationsprozess über die native IDnow-SDK.
	 */
	const startVideoIdentification = () =>
		startIdentification(
			() => $api.identityVerificationApi.createWebIdentificationForRegistration(),
			() => {
				if (!startData.value?.companyId || !startData.value?.transactionToken) {
					throw new Error('Missing companyId or transactionToken from backend')
				}
				return IDnowPlugin.startVideoIdentification({
					companyId: startData.value.companyId,
					transactionToken: startData.value.transactionToken,
				})
			},
		)

	/**
	 * Startet den AutoIdent-Prozess (Face-Ident) über die native IDNowSDKCore.
	 */
	const startAutoIdentification = () =>
		startIdentification(
			() => $api.identityVerificationApi.createWebIdentificationForRegistration({ legitimationType: 'AUTOIDGWG' }),
			() => {
				if (!startData.value?.transactionToken) {
					throw new Error('Missing transactionToken from backend')
				}
				return IDnowAutoIdentPlugin.startAutoIdentification({
					token: startData.value.transactionToken,
				})
			},
		)

	return {
		verificationStatus,
		verificationData,
		isLegitimationEnabled,
		isPolling,
		isLoading,
		fetchStatus,
		startVideoIdentification,
		startAutoIdentification,
		startPolling,
		stopPolling,
	}
}
